import React, { useEffect, useRef, useState } from "react";

import { collection, doc, limitToLast, onSnapshot, orderBy, query, updateDoc, where } from "firebase/firestore";
import { getDownloadURL, ref } from "firebase/storage";
import { httpsCallable } from "firebase/functions";
import { Alert, Anchor, Badge, Button, Group, Image, Loader, Paper, ScrollArea, SegmentedControl, Stack, Text, Textarea } from "@mantine/core";
import { notifications } from "@mantine/notifications";

import { useFirestore, useFunctions, useStorage } from "../../helpers/firebaseContext";
import useFirestoreCollection from "../../helpers/useFirestoreCollection";
import { BaseResult, SendWhatsappChatMessageRequest, WhatsappChatMediaFile, WhatsappChatMessage, WhatsappThread } from "../../types";

// Must match WHATSAPP_SESSION_WINDOW_MS in functions/src/whatsappChat.ts:
// WhatsApp only allows free-form replies within 24h of the client's last message.
const SESSION_WINDOW_MS = 24 * 60 * 60 * 1000;

const statusTicks: Record<string, { label: string; color?: string }> = {
  queued: { label: "🕓" },
  accepted: { label: "🕓" },
  sent: { label: "✓" },
  delivered: { label: "✓✓" },
  read: { label: "✓✓", color: "blue" },
  failed: { label: "failed", color: "red" },
  undelivered: { label: "undelivered", color: "red" },
  canceled: { label: "canceled", color: "red" },
};

function MediaAttachment({ file }: { file: WhatsappChatMediaFile }) {
  const storage = useStorage();
  const [url, setUrl] = useState<string | undefined>(undefined);

  useEffect(() => {
    getDownloadURL(ref(storage, file.path)).then(setUrl).catch(console.error);
  }, [storage, file.path]);

  if (!url) return <Loader size="xs" />;

  if (file.contentType.startsWith("image/")) {
    return <Image src={url} maw={250} radius="sm" />;
  }
  return (
    <Anchor href={url} target="_blank" rel="noreferrer" size="sm">
      📎 Attachment ({file.contentType})
    </Anchor>
  );
}

function ChatMessageBubble({ message }: { message: WhatsappChatMessage }) {
  const outbound = message.direction === "outbound";
  const isTemplate = message.type === "template";
  const ticks = message.status ? statusTicks[message.status] : undefined;

  return (
    <Group justify={outbound ? "flex-end" : "flex-start"}>
      <Paper
        shadow="xs"
        p="xs"
        radius="md"
        maw="70%"
        bg={isTemplate ? "grape.0" : outbound ? "green.1" : "gray.1"}
      >
        <Stack gap={4}>
          {isTemplate && (
            <Group gap={6}>
              <Badge size="xs" variant="light" color="grape">Template</Badge>
              <Text size="xs" c="dimmed">{message.templateName}</Text>
            </Group>
          )}
          {message.mediaFiles?.map((file, i) => <MediaAttachment key={i} file={file} />)}
          {message.body && (
            <Text size="sm" c="dark" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
              {message.body}
            </Text>
          )}
          <Group gap={6} justify="flex-end">
            {outbound && message.sentBy && (
              <Text size="xs" c="dimmed">
                {message.sentBy.name || message.sentBy.email}
              </Text>
            )}
            <Text size="xs" c="dimmed">
              {message.timestamp?.toDate().toLocaleString()}
            </Text>
            {outbound && ticks && (
              <Text size="xs" c={ticks.color ?? "dimmed"}>{ticks.label}</Text>
            )}
          </Group>
          {message.errorMessage && (
            <Text size="xs" c="red">{message.errorMessage}</Text>
          )}
        </Stack>
      </Paper>
    </Group>
  );
}

// Rendered with key={thread.id} so switching threads remounts and resubscribes.
// Also used by the global inbox at /whatsapp.
export function ChatThread({ thread }: { thread: WhatsappThread }) {
  const db = useFirestore();
  const functions = useFunctions();

  const [messages, setMessages] = useState<WhatsappChatMessage[] | undefined>(undefined);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const viewportRef = useRef<HTMLDivElement>(null);

  const threadID = thread.id!;

  useEffect(() => {
    const messagesQuery = query(
      collection(db, "whatsapp_threads", threadID, "messages"),
      orderBy("timestamp", "asc"),
      limitToLast(200),
    );
    const unsubscribe = onSnapshot(messagesQuery, (snapshot) => {
      setMessages(snapshot.docs.map((d) => ({ ...(d.data() as WhatsappChatMessage), id: d.id })));
    });
    return unsubscribe;
  }, [db, threadID]);

  //opening the thread clears the unread counter (rules only allow touching this field)
  useEffect(() => {
    if (thread.unreadCount) {
      updateDoc(doc(db, "whatsapp_threads", threadID), { unreadCount: 0 }).catch(console.error);
    }
  }, [db, threadID, thread.unreadCount]);

  useEffect(() => {
    viewportRef.current?.scrollTo({ top: viewportRef.current.scrollHeight });
  }, [messages]);

  const lastInboundMillis = thread.lastInboundAt?.toMillis();
  const sessionOpen = !!lastInboundMillis && Date.now() - lastInboundMillis < SESSION_WINDOW_MS;

  const sendMessage = async () => {
    const body = draft.trim();
    if (!body) return;
    setSending(true);
    try {
      const send = httpsCallable<SendWhatsappChatMessageRequest, BaseResult>(functions, "sendWhatsappChatMessage");
      await send({ threadID, body });
      setDraft("");
    } catch (e: any) {
      notifications.show({
        title: "WhatsApp message failed",
        message: e?.message ?? "Unknown error",
        color: "red",
      });
    }
    setSending(false);
  };

  if (!messages) return <Loader />;

  return (
    <Stack gap="sm">
      <ScrollArea h={450} viewportRef={viewportRef} type="auto">
        <Stack gap="xs" p="xs">
          {messages.length === 0 && <Text c="dimmed" ta="center">No messages yet</Text>}
          {messages.map((message) => <ChatMessageBubble key={message.id} message={message} />)}
        </Stack>
      </ScrollArea>

      {sessionOpen ? (
        <Group align="flex-end" gap="xs">
          <Textarea
            style={{ flex: 1 }}
            placeholder="Type a reply…"
            autosize
            minRows={1}
            maxRows={4}
            maxLength={1600}
            value={draft}
            onChange={(e) => setDraft(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                sendMessage();
              }
            }}
          />
          <Button onClick={sendMessage} loading={sending} disabled={!draft.trim()}>
            Send
          </Button>
        </Group>
      ) : (
        <Alert color="yellow" variant="light">
          The 24-hour WhatsApp reply window is closed. You can reply once the client sends a new message.
        </Alert>
      )}
    </Stack>
  );
}

export default function CustomerWhatsapp({ customerID }: { customerID: string }) {
  const db = useFirestore();

  const threadsQuery = query(
    collection(db, "whatsapp_threads"),
    where("customerIds", "array-contains", customerID),
  );
  const { loading, data } = useFirestoreCollection(threadsQuery, true);
  const threads = (data ?? []) as WhatsappThread[];

  const [selectedThreadID, setSelectedThreadID] = useState<string | null>(null);

  if (loading || data === undefined) return <Loader />;

  if (threads.length === 0) {
    return (
      <Alert color="gray" variant="light" mt="md">
        No WhatsApp activity with this client yet. A thread appears once the client messages the
        company WhatsApp number, or once a template (KYC link, campaign, notification) is sent to
        their number.
      </Alert>
    );
  }

  const activeThread = threads.find((t) => t.id === selectedThreadID) ?? threads[0];

  return (
    <Stack gap="sm" mt="md">
      {threads.length > 1 && (
        <SegmentedControl
          value={activeThread.id}
          onChange={setSelectedThreadID}
          data={threads.map((t) => ({
            value: t.id!,
            label: t.profileName ? `${t.mobile} (${t.profileName})` : t.mobile,
          }))}
        />
      )}
      <ChatThread key={activeThread.id} thread={activeThread} />
    </Stack>
  );
}
